/**
 * Canonical "portrait" fill for `SideStatPanel.cosmeticSlot`.
 *
 * Today's cosmetic equip surface (per `voxel-cosmetics-plan.md` + the
 * `Agent.cosmetics` schema in `@pilot-protocol/api-types`) supports
 * three voxel slots + an accent color:
 *
 *   {
 *     chassis_skin_id: string | null,
 *     kill_effect_id:  string | null,
 *     deployment_anim_id: string | null,
 *     color: HexColor,
 *   }
 *
 * Engineer-avatar / badge / banner equip slots are spec'd but not yet
 * in the schema (VCQ-2 — voxel-cosmetics-plan.md:321). Until they land,
 * the spectator overlay's "portrait" slot has three realistic fills:
 *
 *   1. **Voxel portrait** — pass `voxJsonPath` resolved from a future
 *      `engineer_avatar_id` (when the schema bumps) OR from the
 *      equipped `chassis_skin_id` as a "your mech is your portrait"
 *      bridge. `<VoxelDisplay>` auto-fits camera to the asset's bbox.
 *
 *   2. **Thumbnail PNG** — pass `thumbSrc` to render a baked 2D
 *      preview (W5 bake script is deferred, so this mode is currently
 *      consumer-supplied — e.g., a stock engineer portrait PNG).
 *
 *   3. **Initials fallback** — always renders when neither voxel nor
 *      thumb is provided. Pure monospace text inside the hairline
 *      frame. Lossy but cheap, and matches the retro-scifi vocabulary.
 *
 * Layering: voxJsonPath > thumbSrc > initials. Consumer chooses the
 * fidelity tier; this component picks one.
 *
 * NOTE: rendering `<VoxelDisplay>` inside an HTML overlay nests a fresh
 * R3F Canvas per side. That's two extra WebGL contexts on a viewer
 * that's already running the main replay Canvas. Acceptable for
 * spectator/app surfaces; for the OBS overlay route (`/overlay/match/
 * :id`, O4) the streamer's machine may be tight — keep `thumbSrc` or
 * initials as the perf-conscious default and let voxel be opt-in.
 */

import type { CSSProperties } from "react";
import { VoxelDisplay } from "../cosmetic/VoxelDisplay";

export interface CosmeticPortraitProps {
  /** Path to a baked `vox_json` asset (e.g. an `engineer_avatar` SKU
   *  or — for a "your mech is your portrait" bridge — the equipped
   *  `chassis_skin` asset). Wins over `thumbSrc`. */
  voxJsonPath?: string;
  /** URL to a 2D thumbnail PNG. Used when `voxJsonPath` is absent. */
  thumbSrc?: string;
  /** Two-letter initials used as the final fallback. Defaults to "—". */
  initials?: string;
  /** Override the accent stripe color (defaults to no stripe). */
  accent?: string;
  /** Lighting preset forwarded to `<VoxelDisplay>` when in voxel mode. */
  voxelLighting?: "p1-basic" | "magica-voxel";
  /** Hide entirely. */
  hidden?: boolean;
}

const containerStyle: CSSProperties = {
  position: "absolute",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden",
};

const accentStripeStyle = (accent: string): CSSProperties => ({
  position: "absolute",
  left: 0,
  top: 0,
  bottom: 0,
  width: 2,
  background: accent,
  pointerEvents: "none",
});

const initialsStyle: CSSProperties = {
  fontFamily: "var(--pp-font-family)",
  fontSize: 28,
  fontWeight: "var(--pp-weight-bold)",
  letterSpacing: "var(--pp-tracking-caps)",
  color: "var(--pp-text)",
  textTransform: "uppercase",
};

const thumbStyle: CSSProperties = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
  filter: "saturate(0.85)",
};

export function CosmeticPortrait({
  voxJsonPath,
  thumbSrc,
  initials = "—",
  accent,
  voxelLighting = "magica-voxel",
  hidden = false,
}: CosmeticPortraitProps) {
  if (hidden) return null;
  let body: React.ReactNode;
  if (voxJsonPath) {
    body = (
      <VoxelDisplay
        path={voxJsonPath}
        width={120}
        height={120}
        lighting={voxelLighting}
        background="transparent"
        orbitEnabled={false}
      />
    );
  } else if (thumbSrc) {
    body = <img src={thumbSrc} alt="" style={thumbStyle} />;
  } else {
    body = <span style={initialsStyle}>{initials.slice(0, 2)}</span>;
  }
  return (
    <div style={containerStyle}>
      {accent ? <div style={accentStripeStyle(accent)} aria-hidden /> : null}
      {body}
    </div>
  );
}
